import React, { useState, useEffect, useRef } from 'react';
import { Send, Star, CheckCheck, Check, MessageSquare, ArrowLeft, X } from 'lucide-react';
import { getUnreadCount, markSingleAsRead } from './ChatService';

export default function ChatWindow({ 
    activeChat, 
    messages = [], 
    onSendMessage, 
    onToggleFavorite, 
    onMarkAllAsRead,
    onCloseChat,
    empId,
    loadingHistory,
    pageViewHandler
}) {
    const [inputText, setInputText] = useState('');
    const [userUnreadCount, setUserUnreadCount] = useState(null);
    const feedEndRef = useRef(null);

    // Fetch specific customer unread count (API #9) when activeChat changes
    useEffect(() => {
        if (activeChat && activeChat.userId) {
            fetchCustomerUnreadCount(activeChat.userId);
        } else {
            setUserUnreadCount(null);
        }
    }, [activeChat, messages.length]);

    const fetchCustomerUnreadCount = async (userId) => {
        try {
            const count = await getUnreadCount(userId);
            const numericCount = typeof count === 'object' ? (count.unreadCount || count.count || 0) : count;
            setUserUnreadCount(numericCount);
        } catch (e) {
            console.error('Error fetching unread count for user:', e);
        }
    };

    // Auto scroll to bottom of the feed when messages load or change
    useEffect(() => {
        if (feedEndRef.current) {
            feedEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const handleSend = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!inputText.trim()) return;
        onSendMessage(inputText.trim());
        setInputText('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend(e);
        }
    };

    const handleMarkSingleMessageRead = async (messageId) => {
        if (!messageId) return;
        try {
            await markSingleAsRead(messageId);
            if (activeChat && activeChat.userId) {
                fetchCustomerUnreadCount(activeChat.userId);
            }
        } catch (e) {
            console.error('Error marking single message as read:', e);
        }
    };

    const formatMessageTime = (timeString) => {
        if (!timeString) return '';
        try {
            const date = new Date(timeString);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return '';
        }
    };

    const formatGroupDate = (timeString) => {
        if (!timeString) return '';
        try {
            const date = new Date(timeString);
            const now = new Date();
            if (date.toDateString() === now.toDateString()) {
                return 'Today';
            }
            const yesterday = new Date(now);
            yesterday.setDate(now.getDate() - 1);
            if (date.toDateString() === yesterday.toDateString()) {
                return 'Yesterday';
            }
            return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
        } catch (e) {
            return '';
        }
    };

    const renderMessageContent = (text) => {
        if (!text) return '';
        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|(?:[a-zA-Z0-9-]+\.)+(?:credfin\.money|money|com|in|io|org)[^\s]*)/gi;
        const parts = text.split(urlRegex);
        return parts.map((part, i) => {
            if (part && part.match(urlRegex)) {
                let href = part;
                if (!href.startsWith('http://') && !href.startsWith('https://')) {
                    href = 'https://' + href;
                }
                return (
                    <a 
                        key={i} 
                        href={href} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="msg-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {part}
                    </a>
                );
            }
            return part;
        });
    };

    if (!activeChat) {
        return (
            <div 
                className="chat-window empty-window"
                style={{
                    flexGrow: 1,
                    width: '100%',
                    height: '100%',
                    minHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8fafc',
                    backgroundImage: 'radial-gradient(at 0% 0%, rgba(13, 122, 110, 0.05) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(13, 122, 110, 0.05) 0px, transparent 50%)',
                    padding: '32px',
                    boxSizing: 'border-box'
                }}
            >
                <div 
                    className="empty-card"
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '24px',
                        padding: '44px 36px',
                        maxWidth: '440px',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(226, 232, 240, 0.6)',
                        boxSizing: 'border-box',
                        margin: 'auto'
                    }}
                >
                    <div 
                        className="empty-icon-badge"
                        style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #e6f4f1 0%, #ccece6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'inset 0 0 0 1px rgba(13, 122, 110, 0.15), 0 8px 16px -4px rgba(13, 122, 110, 0.15)',
                            marginBottom: '12px'
                        }}
                    >
                        <MessageSquare size={38} color="#0d7a6e" style={{ width: 'auto', margin: 0 }} />
                    </div>
                    <div 
                        className="empty-title"
                        style={{
                            fontSize: '22px',
                            fontWeight: '800',
                            color: '#0f172a',
                            letterSpacing: '-0.4px',
                            marginTop: '12px',
                            marginBottom: '8px'
                        }}
                    >
                        Select a Conversation
                    </div>
                    <div 
                        className="empty-desc"
                        style={{
                            fontSize: '14px',
                            color: '#64748b',
                            lineHeight: '1.6',
                            maxWidth: '340px',
                            margin: '0 auto'
                        }}
                    >
                        Choose a loan application from the sidebar to view complete message history and interact with customers.
                    </div>
                    {pageViewHandler && (
                        <button 
                            className="back-landing-btn"
                            onClick={(e) => pageViewHandler(e, 'landingPage')}
                            style={{
                                marginTop: '28px',
                                padding: '12px 24px',
                                borderRadius: '12px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #0d7a6e 0%, #0b665c 100%)',
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 14px rgba(13, 122, 110, 0.3)'
                            }}
                        >
                            <ArrowLeft size={16} style={{ width: 'auto',margin:'0' }} /> Back to Dashboard
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const isStarred = activeChat.favorite === true || activeChat.isFavorite === true;

    // Group messages by date
    let lastDateLabel = '';

    return (
        <div className="chat-window">
            {/* Window Header */}
            <div className="window-header">
                <div className="header-info">
                    <span className="header-title">
                        {activeChat.name || activeChat.userName || activeChat.customerName || `Application: ${activeChat.applicationId}`}
                    </span>
                    <div className="header-subtitle-row">
                        <span className="header-subtitle">
                            App ID: <strong>{activeChat.applicationId}</strong>
                        </span>
                        <span className="header-subtitle">
                            User ID: <strong>{activeChat.userId || 'N/A'}</strong>
                        </span>
                        {userUnreadCount !== null && userUnreadCount > 0 && (
                            <span className="unread-pill-tag">
                                {userUnreadCount} unread
                            </span>
                        )}
                    </div>
                </div>

                <div className="header-actions">
                    <button 
                        className={`header-btn ${isStarred ? 'star-active' : ''}`}
                        onClick={() => onToggleFavorite(activeChat.applicationId)}
                        title="Toggle Favorite (API #6)"
                    >
                        <Star size={14} fill={isStarred ? '#f59e0b' : 'none'} color={isStarred ? '#f59e0b' : '#cbd5e1'}  style={{width:'auto'}} />
                        {isStarred ? 'Starred' : 'Star App'}
                    </button>
                    
                    <button 
                        className="header-btn"
                        onClick={() => {
                            onMarkAllAsRead(activeChat.userId);
                            setUserUnreadCount(0);
                        }}
                        title="Mark All as Read (API #5)"
                    >
                        <CheckCheck size={14} color="#10b981" style={{width:'auto'}} />
                        Mark All Read
                    </button>

                    {onCloseChat && (
                        <button 
                            className="header-btn close-chat-btn" 
                            onClick={onCloseChat}
                            title="Close Chat Window (Press Esc)"
                        >
                            <X size={16} color="#64748b" />
                        </button>
                    )}
                </div>
            </div>

            {/* Message Feed */}
            <div className="message-feed">
                {loadingHistory ? (
                    <div className="loading-messages">
                        <i className="fa fa-spinner fa-spin fa-2x spinner-brand"></i>
                        <div>Loading message history...</div>
                    </div>
                ) : (
                    <>
                        {messages.map((msg, index) => {
                            const isOutgoing = msg.senderType === 'ADMIN' || msg.senderId === 'ADMIN' || msg.senderId === 'SuperUser' || msg.senderId === empId;
                            const dateLabel = formatGroupDate(msg.createdAt || msg.addedOn);
                            let showDateDivider = false;
                            if (dateLabel && dateLabel !== lastDateLabel) {
                                showDateDivider = true;
                                lastDateLabel = dateLabel;
                            }

                            return (
                                <React.Fragment key={msg.id || msg.messageId || index}>
                                    {showDateDivider && (
                                        <div className="date-divider">
                                            <span>{dateLabel}</span>
                                        </div>
                                    )}
                                    <div className={`msg-row ${isOutgoing ? 'outgoing' : 'incoming'}`}>
                                        <div className="msg-bubble">
                                            <div className="msg-content">
                                                {renderMessageContent(msg.comment || msg.message || msg.latestMessage)}
                                            </div>
                                            <div className="msg-meta">
                                                <span>{formatMessageTime(msg.createdAt || msg.addedOn)}</span>
                                                {isOutgoing ? (
                                                    <CheckCheck size={14} color="#6ee7b7"  style={{width:'auto',margin:'0'}} />
                                                ) : (
                                                    (msg.id || msg.messageId) && (
                                                        <button 
                                                            className="single-read-btn"
                                                            onClick={() => handleMarkSingleMessageRead(msg.id || msg.messageId)}
                                                            title="Mark single message as read (API #8)"
                                                        >
                                                            <Check size={12} color="#94a3b8"  style={{width:'auto',margin:'0'}} />
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        <div ref={feedEndRef} />
                    </>
                )}
            </div>

            {/* Input Bar */}
            <div className="footer-input-bar">
                <form className="chat-form" onSubmit={handleSend}>
                    <input 
                        type="text" 
                        placeholder="Type a message..." 
                        className="chat-input"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button type="submit" className="send-btn" title="Send Message (API #4)">
                        <Send size={16} />
                    </button>
                </form>
            </div>
        </div>
    );
}
